import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import VideoPlayer from '@/components/VideoPlayer';
import { getYouTubeVideoId } from '@/utils/youtube';
import { Pencil, Trash2, GripVertical } from 'lucide-react';

interface PresentationVideo {
  id: string;
  title: string;
  description: string | null;
  youtube_url: string;
  category: string;
  display_order: number;
}

interface SortableVideoCardProps {
  video: PresentationVideo;
  isAdmin: boolean;
  onEdit: (video: PresentationVideo) => void;
  onDelete: (video: PresentationVideo) => void;
}

export const SortableVideoCard = ({ video, isAdmin, onEdit, onDelete }: SortableVideoCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const videoId = getYouTubeVideoId(video.youtube_url);

  return (
    <Card 
      ref={setNodeRef} 
      style={style as React.CSSProperties}
      className={`overflow-hidden ${isDragging ? 'shadow-xl ring-2 ring-golf-500' : ''}`}
    >
      <div className="aspect-video relative">
        {videoId && <VideoPlayer videoId={videoId} />}
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg">{video.title}</CardTitle>
          {isAdmin && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(video)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onDelete(video)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        {video.description && (
          <CardDescription className="line-clamp-6">
            {video.description}
          </CardDescription>
        )}
      </CardHeader>
    </Card>
  );
};
